import moment from 'moment';
import Header from './Header';
import dynamic from 'next/dynamic';
import {useRouter} from 'next/router';
import ApplyBtns, {ApplyButton} from './ApplyButtons';
import {memo, useEffect, useMemo, useRef, useState} from 'react';
import {cleanUrl, isCSR} from '@jobseeker/lotus/utils';
import {Box, Button, Skeleton, Typography} from '@mui/material';
import {JOBDETAIL, ROUTE_JOB_DETAIL} from '@constants';
import {VersionVariants} from '@jobseeker/lotus/enums';
import type {AppState, JobDetail} from '@jobseeker/common-core/types';
import {handleJobApplicationOnHirist} from '@utils/applyJobUtils';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {
  Clients,
  CompanyInsights,
  ConsultantStrip,
  DialogWithBottomSheet,
  JobOverviewCard,
  Loader,
} from '@jobseeker/lotus/components';
import useInterceptor from '@jobseeker/common-core/hooks/useInterceptor';
import {
  getUbaPageName,
  handleTracking,
  openUrl,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';
import type {JobDetail as JB} from '@jobseeker/common-core/stateModels/job/jobdetail.models';
import {
  fetchSimilarJobs,
  saveJob,
} from '@jobseeker/common-core/reducers/job/thunkServices/jobfeed';
import {
  fetchJobInsights,
  getMagicRank,
} from '@jobseeker/common-core/reducers/job/thunkServices/jobdetail';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  ConfidentialIcon,
  ConfidentialTag,
  DefaultJobLogo,
  Dot,
  HiristInfoIcon,
  ThreeStar,
  VerifiedIcon,
} from '@jobseeker/lotus/assets/icons';
import {ReportJobDialog} from '@jobseeker/feature-job/components';
import {jobDetailService} from '@jobseeker/common-core/services';
import {useIsCustomWidth, useIsLargeDevice} from '@jobseeker/lotus/hooks';
import BlogsPanel from '@jobseeker/feature-ads/components/BlogsPanel';
import JobsByLocation from './JobsByLocation';
import InjectSearchOverlay from '@components/SearchInjection';
import Diversity from './Diversity';
import {checkIsTechDrive} from './utils';
import {HIRIST_FUNCTIONAL_AREAS} from '@jobseeker/feature-onboarding/constants';
import ProfileUpdateModal from '@jobseeker/feature-profile/components/ProfileUpdateModal';
import JobPostingJsonLd from '@jobseeker/feature-job/components/JobPostingSchema';
import {
  Wrapper,
  CompanyAwards,
  CompanyJobs,
  CompanyHighLights,
  AboutUsSection,
  JDBrandingBanner,
  EmployerBenefits,
  CompanyReview,
  StickyJobNavigation,
} from '@jobseeker/lotus/components';
import {ImageWithFallback} from '@jobseeker/lotus/components/JobList/V2';
import {getLocationString} from '@jobseeker/feature-job/utils';
import {BrandedJDData} from '@jobseeker/lotus/components';
import {getRandomColor} from '@jobseeker/lotus/theme';

const getQueryParams = () => {
  if (typeof window === 'undefined') {
    return {};
  }
  return Object.fromEntries(new URLSearchParams(window?.location?.search));
};

const InsightsModal = dynamic(
  () => import('@jobseeker/feature-job/components/InsightsModal/V2'),
  {
    loading: () => <Loader />,
    ssr: false,
  }
);

const AdsPanelHarness = memo(
  dynamic(
    () => import('@jobseeker/client-harness/components/AdsHarness/AdsPanelV2'),
    {
      loading: () => (
        <Box minHeight={1720} backgroundColor={'white'}>
          <Loader />
        </Box>
      ),
      ssr: false,
    }
  )
);

const JobFeedHarness = dynamic(
  () => import('@jobseeker/client-harness/components/JobHarness/JobfeedV2'),
  {
    loading: () => <Loader />,
  }
);

const HiristAuthentication = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/HiristAuthenticationHarness/AuthForm'
    ),
  {
    ssr: false,
  }
);

const DownloadAppJobdetail = dynamic(
  () => import('@jobseeker/lotus/components/DownloadAppJobdetailAndCatogary'),
  {
    loading: () => <Loader />,
    ssr: false,
  }
);

// const ResumePopup = dynamic(
//   () =>
//     import(
//       '@jobseeker/client-harness/components/ProfileHarness/ResumePopup'
//     ),
//   {
//     loading: () => <Loader />,
//     ssr: false,
//   }
// );

type Testimonial = {
  image: string;
  name: string;
  about: string;
};

type DiversityData = {
  diversityId: number;
  diversityName: string;
  diversityUrl: string;
  testimonials: Testimonial[];
};

const getVideoId = (url = '') => {
  const regExp =
    /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[7].length == 11 ? match[7] : '';
};

type JobDetailProps = {
  jobId: string;
  categoryName: string;
  refInfo: string | undefined;
  isMobile?: boolean;
  brandedData: BrandedJDData;
  brandedJD?: number;
  similarJobs?: JobDetail[];
};

const HiristJobDetail = (props: JobDetailProps) => {
  const {
    jobId,
    categoryName,
    refInfo,
    isMobile = false,
    brandedData,
    similarJobs,
  } = props;

  const jobInfo = useSelector((state: AppState) => state.jobDetail) as JB & {
    magicRank: number;
    careerCompanyName?: string;
    hasExpired?: boolean;
    permanentlyRemoved?: boolean;
  };

  const {
    userId = '',
    isLoggedIn = false,
    isPro,
  } = useSelector((state: AppState) => state.auth);

  const userData = useSelector((state: AppState) => state.user, shallowEqual);
  const page = useSelector((state: AppState) => state.app.page, shallowEqual);
  const isSearchEnabled = useSelector(
    (state: AppState) => state.app.layout.searchOverlay,
    shallowEqual
  );
  useEffect(() => {
    jobDetailService.markJobViewed(jobId);
  }, []);

  const {
    min,
    max,
    hits,
    tags,
    title,
    active,
    premium,
    videoUrl,
    showcase,
    diversity,
    exDefence,
    locations,
    magicRank,
    applyCount,
    categoryId,
    createdTime,
    tagIdString,
    confidential,
    workFromHome,
    functionalArea,
    femaleCandidate,
    differentlyAbled,
    hasExpired = false,
    followUpStatus = 0,
    femaleBackWorkForce,
    recruiter: {recruiterId = 0} = {recruiterId: 0},
  } = jobInfo;

  const {diversityUrl = '', testimonials = []} = (diversity ??
    {}) as DiversityData;

  const {image, name: fullName, about} = testimonials?.[0] ?? {};

  const [name, desig] = fullName?.split(',') ?? ['', ''];

  const diversityData = {
    diversityUrl,
    image,
    about: about?.slice(0, 400) + '...',
    name: name.trim(),
    designation: desig?.trim(),
  };

  const diversityArray = [];
  if (femaleCandidate) diversityArray.push('Women candidates preferred');
  if (differentlyAbled)
    diversityArray.push('Differently-abled candidates preferred');
  if (exDefence) diversityArray.push('Ex-defence personnel preferred');
  if (workFromHome) diversityArray.push('May work from home');
  if (femaleBackWorkForce)
    diversityArray.push('For women joining back the workforce');

  // const {
  //   profile: {resumeUpdated, email},
  // } = userData;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  // Individual expansion state management for each section
  const [jobOverviewExpanded, setJobOverviewExpanded] = useState(false);
  const [brandedBenefitsExpanded, setBrandedBenefitsExpanded] = useState(false);
  const [companyJobsExpanded, setCompanyJobsExpanded] = useState(false);
  const [companyHighlightsExpanded, setCompanyHighlightsExpanded] =
    useState(false);

  const toggleJobOverview = () => setJobOverviewExpanded(prev => !prev);
  const toggleBrandedBenefits = () => setBrandedBenefitsExpanded(prev => !prev);
  const toggleCompanyJobs = () => setCompanyJobsExpanded(prev => !prev);
  const toggleCompanyHighlights = () =>
    setCompanyHighlightsExpanded(prev => !prev);

  const [insightsOpen, setInsightsOpen] = useState(false);
  const {enableLoginPopup = false} = useSelector(
    (state: AppState) => state.app.layout
  );
  const [openReportJobDialog, setOpenReportJobDialog] = useState(false);
  const [followup, setFollowedUp] = useState(followUpStatus !== 0);
  const [openResumeBanner, setOpenResumeBanner] = useState(false);
  const [isProfileUpdateModalOpen, setIsProfileUpdateModalOpen] =
    useState(false);
  // const [resumeBannerSuccess, setResumeBannerSuccess] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [jobApplied, setJobApplied] = useState(!!jobInfo.applied);
  const [applyBtnClicked, setApplyBtnClicked] = useState(false);
  const [openConfJobDialogHirist, setOpenConfJobDialogHirist] = useState(false);

  const targetRef = useRef(null);

  const isDeviceLarge = useIsLargeDevice('md');
  const isDeviceMedium = useIsLargeDevice();
  const isSmall = useIsCustomWidth('sm');
  const IsLarge = useIsLargeDevice('sm');

  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  useEffect(() => {
    if (!jobId || !userId) return;

    const callApis = () => {
      Promise.all([
        dispatch(
          fetchSimilarJobs({
            userId,
            tags: tagIdString ? tagIdString.split(' ') : [],
            exp: min,
            min,
            max,
            id: jobId,
            cat: categoryId,
            whatFor: 'hirist',
            functionalArea,
          })
        ),
        userId &&
          dispatch(
            getMagicRank({jobId, userId: parseInt(userId), headers: {}})
          ),
      ]).catch(error => console.log('🚀 ~ Error fetching job data:', error));
    };

    const DEBOUNCE_DELAY = 250;
    const timer = setTimeout(callApis, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [
    jobId,
    userId,
    tagIdString,
    min,
    categoryId,
    dispatch,
    functionalArea,
    max,
  ]);
  const isBranded = props.brandedJD === 1;

  const [expanded, setExpanded] = useState(false);
  const {device} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );
  const isLargeDevice = device == 'desktop';

  const jobDescriptionRef = useRef<HTMLDivElement>(null);
  const aboutCompanyRef = useRef<HTMLDivElement>(null);
  const companyJobsRef = useRef<HTMLDivElement>(null);
  const companyHighlightsRef = useRef<HTMLDivElement>(null);
  const InsightsRef = useRef<HTMLDivElement>(null);
  const companyAwardsRef = useRef<HTMLDivElement>(null);
  const companyBenefitsRef = useRef<HTMLDivElement>(null);
  const companyReviewsRef = useRef<HTMLDivElement>(null);

  // Navigation sections configuration
  const navigationSections = [
    {
      id: 'job-description',
      label: 'Job Detail',
      ref: jobDescriptionRef,
      tag: 'JobDetail',
    },
  ];

  if (isBranded && brandedData?.aboutCompany?.visibility) {
    navigationSections.push({
      id: 'about-company',
      label: 'About The Company',
      ref: aboutCompanyRef,
      tag: 'AboutCompany',
    });
  }

  if (isBranded && brandedData?.benefits?.visibility) {
    navigationSections.push({
      id: 'company-benefits',
      label: 'Benefits',
      ref: companyBenefitsRef,
      tag: 'Benefits',
    });
  }

  if (isBranded && brandedData?.reviews?.visibility) {
    navigationSections.push({
      id: 'review-company',
      label: 'Reviews',
      ref: companyReviewsRef,
      tag: 'Reviews',
    });
  }

  const toggleExpand = () => setExpanded(prev => !prev);
  const [ABDesc, setABDesc] = useState<string>(
    !isBranded || expanded
      ? jobInfo?.introText
      : jobInfo?.introText.substr(0, 1200).trim()
  );
  const {jobVersion = '', jobPos = '', ref = ''} = getQueryParams();
  const getAwardsCard = () => {
    return (
      <>
        {' '}
        <Typography
          sx={{fontWeight: 600, fontSize: 16, pb: 1, px: {xxs: 2, md: 0}}}
        >
          {' '}
          {brandedData?.awardsRecognitions?.awardsHeading ||
            'Awards & Recognition'}
        </Typography>
        <Wrapper
          heading={<></>}
          component={
            <CompanyAwards
              awards={brandedData?.awardsRecognitions?.data?.awardsList || []}
              jobInfo={jobInfo}
              jdQueryParams={{ref: ref || '', jobPos: jobPos || ''}}
            />
          }
          isAppIimjobs={isAppIimjobs}
        />
      </>
    );
  };

  const getVerifiedBenefitsCard = () => {
    return (
      <Box mb={brandedData?.reviews?.visibility ? 0 : {xxs: 50, md: 2}}>
        <Box
          display="flex"
          alignItems="center"
          gap={0.75}
          pb={1}
          px={{xxs: 2, md: 0}}
        >
          <Image src={VerifiedIcon} alt="verified" height={18} width={18} />
          <Typography sx={{fontWeight: 600, fontSize: 16}}>
            {brandedData?.benefits?.benefitsHeading ||
              'Company Verified Benefits'}
          </Typography>
        </Box>

        <Wrapper
          heading={<></>}
          component={
            <EmployerBenefits
              benefits={brandedData?.benefits?.data?.benefitsList || []}
              isAppIimjobs={isAppIimjobs}
              isExpanded={brandedBenefitsExpanded}
              onToggle={toggleBrandedBenefits}
              heading={
                brandedData?.benefits?.benefitsHeading ||
                'Company Verified Benefits'
              }
              jobInfo={jobInfo}
              jdQueryParams={{ref: ref || '', jobPos: jobPos || ''}}
            />
          }
          isAppIimjobs={isAppIimjobs}
        />
      </Box>
    );
  };

  const getCompanyReview = () => {
    return (
      brandedData?.reviews?.data && (
        <Box mb={{xxs: 50, md: 4}}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent={'space-between'}
            gap={0.75}
            pb={1}
            px={{xxs: 2, md: 0}}
          >
            <Box display={'flex'} alignItems="center" gap={0.75}>
              <Image src={ThreeStar} alt="verified" height={22} width={22} />
              <Typography sx={{fontWeight: 600, fontSize: 16}}>
                Reviews{' '}
              </Typography>
            </Box>
            <Typography
              component={'a'}
              href={
                brandedData?.reviews?.data?.reviewData[0]?.reviewLink || '#'
              }
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
              sx={{
                fontSize: 14,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              {`All ${brandedData?.reviews?.data?.reviewData[0]?.reviewsCount} Reviews`}
            </Typography>
          </Box>
          <Wrapper
            heading={<></>}
            component={
              <CompanyReview
                data={brandedData?.reviews?.data}
                jobInfo={jobInfo}
                jdQueryParams={{ref: ref || '', jobPos: jobPos || ''}}
                isAppIimjobs={isAppIimjobs}
              />
            }
            isAppIimjobs={isAppIimjobs}
          />
        </Box>
      )
    );
  };

  const getCompanyJobs = () => {
    return (
      (similarJobs?.length || 0) > 0 && (
        <>
          <Typography
            sx={{fontWeight: 600, fontSize: 16, pb: 1, px: {xxs: 2, md: 0}}}
          >
            {`Discover similar roles at ${brandedData?.aboutCompany?.companyName}`}
          </Typography>

          <Wrapper
            heading={<></>}
            component={
              <CompanyJobs
                jobs={similarJobs || []}
                companyName={brandedData?.aboutCompany?.companyName || ''}
                isExpanded={companyJobsExpanded}
                onToggle={toggleCompanyJobs}
                jobInfo={jobInfo}
                jdQueryParams={{ref: ref || '', jobPos: jobPos || ''}}
                isAppIimjobs={isAppIimjobs}
              />
            }
            isAppIimjobs={isAppIimjobs}
          />
        </>
      )
    );
  };
  const getClients = () => {
    return (
      <>
        <Typography
          sx={{fontWeight: 600, fontSize: 16, pb: 1, px: {xxs: 2, md: 0}}}
        >
          {' '}
          {brandedData?.clients?.heading}
        </Typography>
        <Wrapper
          heading={<></>}
          component={
            <Clients
              Clients={brandedData?.clients?.data || []}
              templateInfo={brandedData?.templateInfo}
              jobInfo={jobInfo}
              jdQueryParams={{ref: ref || '', jobPos: jobPos || ''}}
              isAppIimjobs={isAppIimjobs}
            />
          }
          isAppIimjobs={isAppIimjobs}
        />
      </>
    );
  };

  const getInsights = () => {
    return (
      <>
        <Typography
          sx={{fontWeight: 600, fontSize: 16, pb: 1, px: {xxs: 2, md: 0}}}
        >
          {' '}
          {brandedData?.hiringTrends?.heading}
        </Typography>
        <Wrapper
          heading={<></>}
          component={
            <CompanyInsights
              insights={brandedData?.hiringTrends?.insights}
              templateInfo={brandedData?.templateInfo}
              jobInfo={jobInfo}
              jdQueryParams={{ref: ref || '', jobPos: jobPos || ''}}
              isAppIimjobs={isAppIimjobs}
            />
          }
          isAppIimjobs={isAppIimjobs}
        />
        <Typography
          mb={1.25}
          mt={-1.5}
          ml={{xxs: 2, md: 0}}
          fontSize={10}
          fontWeight={400}
          color={'#6E7287'}
        >
          {brandedData?.sourceAttribution?.sourceAttributionText}
        </Typography>
      </>
    );
  };
  const randomColor = useMemo(
    () => getRandomColor(jobInfo?.id),
    [jobInfo?.companyData]
  );

  const getHighLights = () => {
    return (
      <>
        <Typography
          sx={{fontWeight: 600, fontSize: 16, pb: 1, px: {xxs: 2, md: 0}}}
        >
          {brandedData?.keyHighlights?.keyHighlightsHeading ||
            `Key Highlights at ${brandedData?.aboutCompany?.companyName}`}
        </Typography>
        <Wrapper
          heading={<></>}
          component={
            <CompanyHighLights
              highLights={
                brandedData?.keyHighlights?.data?.keyHighlightsList || []
              }
              heading={
                brandedData?.keyHighlights?.keyHighlightsHeading ||
                `Key Highlights at ${brandedData?.aboutCompany?.companyName}`
              }
              isAppIimjobs={isAppIimjobs}
              isExpanded={companyHighlightsExpanded}
              onToggle={toggleCompanyHighlights}
              jobInfo={jobInfo}
              jdQueryParams={{ref: ref || '', jobPos: jobPos || ''}}
            />
          }
          isAppIimjobs={isAppIimjobs}
        />
      </>
    );
  };

  const router = useRouter();

  // useEffect(() => {
  //   if (resumeUpdated && email && resumeBannerSuccess) {
  //     setOpenResumeBanner(false);
  //   }
  // }, [resumeUpdated, email, resumeBannerSuccess]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setShowApply(!entry.isIntersecting),
      {
        threshold: 0,
        rootMargin: isBranded ? '-290px 0px 0px 0px' : ' 0px 0px 0px 0px',
      }
    );

    const temp = targetRef.current;

    if (temp) {
      observer.observe(temp);
    }

    return () => {
      if (temp) {
        observer.unobserve(temp);
      }
    };
  }, []);

  useEffect(() => {
    const sendTrackingEvents = () => {
      const userId = sessionStorage.getItem('userId') || '';

      handleTracking('ViewJobDescription', {
        event_category: 'Job',
        Source: 'JobDescription',
        UserId: userId,
        Jobid: jobId,
        RecruiterId: recruiterId,
        Device: sessionStorage.getItem('device') || 'desktop',
        Origin: router.pathname,
        Status: sessionStorage.getItem('status') || 'LoggedOut',
        CategoryId: categoryId,
        Ref: ref,
        content_id: jobId,
        content_type: 'product',
      });

      if (showcase) {
        handleTracking('scViewCompanyShowcaseImage', {
          event_category: 'Showcase',
          Source: 'JobDescription',
          CompanyId: showcase.showcaseCompanyId || 0,
          UserId: userId,
          Jobid: jobId,
          RecruiterId: recruiterId,
          Device: sessionStorage.getItem('device') || 'desktop',
          Origin: router.pathname,
          Status: sessionStorage.getItem('status') || 'LoggedOut',
        });

        TrackEventUBA('companyBannerView', 'click', {
          page: getUbaPageName(),
          jobID: jobId || '',
          categoryID: categoryId,
          tagID: '',
          companyID: showcase?.showcaseCompanyId || '',
          companyName: showcase?.showcaseName || '',
          sessionUsername: userId,
        });
      }
    };

    const timeoutId = setTimeout(sendTrackingEvents, 500);

    if (
      (typeof navigator !== 'undefined' && navigator.onLine) ||
      typeof navigator === 'undefined'
    ) {
      const isTechDrive = checkIsTechDrive();

      if (isTechDrive) {
        handleTracking('JD Page View', {
          event_category: 'Offline Landing page',
          view: 1,
        });
      }
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    categoryId,
    categoryName,
    jobId,
    jobPos,
    jobVersion,
    recruiterId,
    ref,
    title,
    router.pathname,
    showcase,
    jobInfo?.companyData?.companyName,
  ]);

  /**
   * Handles the apply flow after a user logs in by clicking the Apply button.
   * Specifically addresses the scenario where the logged-out user initiates the apply action,
   * logs in with an account that has already applied to the job,
   * and the application loader gets stuck indefinitely.
   *
   * This function checks the post-login state and ensures that the UI behaves appropriately
   * if the user has already applied to the job.
   */
  const handleApplyLoaderForReAppliedUser = () => {
    setShowLoader(false);
    setJobApplied(true);
  };

  const handleApply = () => {
    setTimeout(() => {
      TrackEventUBA('jdPageClick', 'click', {
        section: 'Apply',
        ref: ref || '',
        pageName: 'Job Description Page',
        jobID: jobInfo.id || '',
        jobVersion: jobVersion || '',
        jobPos: jobPos || '',
        page: 'JD',
        sessionUsername: sessionStorage.getItem('userId') || '',
      });

      handleJobApplicationOnHirist({
        router,
        jobInfo,
        refInfo,
        userData,
        isLoggedIn,
        performLogoutAction,
        setOpenResumeBanner,
        handleApplyLoaderForReAppliedUser,
        setIsProfileUpdateModalOpen,
      });
    }, 0);
  };

  useEffect(() => {
    if (!applyBtnClicked || !isLoggedIn || !loginSuccess || !userData) {
      return;
    }

    handleApply();
  }, [isLoggedIn, loginSuccess, userData, applyBtnClicked]);

  const jobFeedStore = useSelector(
    (state: AppState) => state.job,
    shallowEqual
  );

  const {
    proMembership, // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }: any = useInterceptor();

  if (!jobInfo.companyData || !jobInfo.recruiter) {
    return <Header />;
  }

  const {
    companyData: {companyName = '', companyStatus, companyLogoFilePath = ''},
    recruiter: {
      recruiterName,
      profilePicUrl,
      designation,
      lastActive = '',
      recruiterActions,
    },
  } = jobInfo;

  const experience = `${min} - ${max} Years`;

  const postedOn = moment(createdTime).format('DD/MM/YYYY');

  const routeToJobDetail = (jobSlug: string, jobId: string | number) => {
    openUrl(`${ROUTE_JOB_DETAIL}/${jobSlug}-${jobId}`);
  };

  const performLogoutAction = () => {
    dispatch({
      type: 'AppContext/set_login_popup',
      payload: true,
    });
  };

  const openInsightsDialog = () => {
    setTimeout(() => {
      if (!isLoggedIn) {
        performLogoutAction();
        return;
      }

      if (isPro) {
        dispatch(
          fetchJobInsights({
            userId: userId,
            jobId: jobId,
          })
        );
      }

      setInsightsOpen(true);

      handleTracking('ClickInsights', {
        JobId: jobId,
        Ref: ref,
        Origin: router.pathname,
        event_category: 'Job',
        Source: 'JobDescription',
        UserId: sessionStorage.getItem('userId') ?? 'NA',
        Device: sessionStorage.getItem('device') || 'desktop',
        Status: sessionStorage.getItem('status') || 'LoggedOut',
        CategoryId: categoryId,
        RecruiterId: recruiterId,
      });

      TrackEventUBA('jdPageClick', 'click', {
        section: 'Insights',
        jobID: jobId,
        ref,
        jobPos: jobPos,
        jobVersion: jobVersion,
        sessionUsername: sessionStorage.getItem('userId') || '',
      });
    }, 0);
  };

  const onJobSave = (jobId: number) => {
    dispatch(
      saveJob({
        jobId,
        unsave: !!jobInfo.saved,
        userId,
      })
    );
  };

  const saveJobForLogged = () => {
    setTimeout(() => {
      if (!isLoggedIn) {
        performLogoutAction();
        return;
      }
      onJobSave(jobInfo.id);
    }, 0);
  };

  const sendEvent = (
    eventCatogary: string,
    eventName: string,
    action: string
  ) => {
    handleTracking(`${eventName}_${action}`, {
      event_category: `${eventCatogary}`,
      Origin: page,
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
      Status: sessionStorage.getItem('status') || 'LoggedOut',
      Php_SessionID_Mod: sessionStorage.getItem('phpSessId') || '0',
    });
  };

  const handleJobFollowup = (jobId: string) => {
    setTimeout(() => {
      handleTracking('ClickFollowUp', {
        event_category: 'Job',
        Origin: router.pathname,
        Source: 'JobDescription',
        CategoryId: jobInfo.categoryId,
        JobId: jobInfo.id,
        RecruiterId: jobInfo.recruiter?.recruiterId,
        UserId: sessionStorage.getItem('userId') || 'NA',
        Device: sessionStorage.getItem('device') || 'desktop',
        Device_Type: sessionStorage.getItem('deviceType') || 'web',
        Status: sessionStorage.getItem('status') || 'LoggedOut',
      });
      TrackEventUBA('jdPageClick', 'click', {
        section: 'Followup',
        pageName: 'Job Description Page',
        jobID: jobInfo.id,
        jobVersion: jobVersion || '',
        jobPos: jobPos || '',
        page: 'JD',
        sessionUsername: sessionStorage.getItem('userId') || '',
      });
      if (!isLoggedIn) {
        performLogoutAction();
        return;
      }

      if (jobInfo?.applied) {
        (async () => {
          try {
            const followUp = await jobDetailService.doFollowUp({jobId});
            if (typeof followUp === 'boolean') {
              // await userService.updateFollowUp();
              setFollowedUp(followUp);
            }
          } catch (error: unknown) {
            console.log('🚀 ~ file: index.tsx:298 ~ error:', error);
          }
        })();
      }
    }, 0);
  };

  const onLoginSuccess = () => {
    if (isCSR()) {
      if (showApply) {
        dispatch({
          type: 'AppContext/set_login_popup',
          payload: false,
        });

        dispatch({
          type: 'Auth/set_user_loggedin',
        });

        setLoginSuccess(true);

        return;
      }
      window.location.reload();
    }
  };

  return (
    <>
      <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />
      {jobInfo && jobInfo?.id && <JobPostingJsonLd jobInfo={jobInfo} />}
      {!isDeviceLarge && showApply && !!active && (
        <Box
          position="fixed"
          bottom="0"
          width="100%"
          zIndex={100}
          paddingLeft={isDeviceLarge ? 0 : 2.5}
          paddingRight={isDeviceLarge ? 0 : 2.5}
        >
          <ApplyButton
            handleApply={handleApply}
            saveJobForLogged={saveJobForLogged}
            openInsightsDialog={openInsightsDialog}
            handleJobFollowup={handleJobFollowup}
            jobSaved={!!jobInfo.saved}
            jobApplied={jobApplied}
            jobFollowedUp={followup}
            jobId={jobId}
            showLoader={showLoader}
            setShowLoader={setShowLoader}
            setApplyBtnClicked={setApplyBtnClicked}
          />
        </Box>
      )}
      {isBranded && (
        <>
          {jobInfo && jobInfo?.id && brandedData?.banner?.visibility ? (
            <JDBrandingBanner
              bannerUrl={
                brandedData?.banner?.preferenceKey == 'bannerVideo'
                  ? brandedData?.banner?.bannerVideo?.path || ''
                  : brandedData?.banner?.bannerImage?.path || ''
              }
              isAppIimjobs={isAppIimjobs}
              templateInfo={brandedData?.templateInfo}
              thumbnail={brandedData?.banner?.bannerVideo?.thumbnailPath || ''}
              jobInfo={jobInfo}
              jdQueryParams={{ref: ref || '', jobPos: jobPos || ''}}
            />
          ) : (
            <Skeleton
              variant="rectangular"
              sx={{
                aspectRatio: '24/5',
                minHeight: {xxs: 82, md: 213, lg: 266, xl: 310},
                mb: {xxs: -2, md: -4.5},
                mt: -2,
                ml: {xxs: -2, md: 0},
                width: '100%',
                borderRadius: 0,
              }}
            />
          )}
        </>
      )}

      <Box ref={targetRef}>
        {!active ? (
          <Header showOnlyHeader />
        ) : (
          <Header
            brandedJob={isBranded}
            title={title}
            company={companyName}
            confidential={confidential}
            locations={locations}
            experience={experience}
            tags={tags.map(tag => tag.name)}
            postedOn={postedOn}
            showcase={showcase ?? {}}
            premium={premium}
            active={Boolean(active) && !hasExpired}
            companyStatus={companyStatus}
            companyLogoFilePath={companyLogoFilePath}
            isMobile={isMobile}
            jobData={jobInfo}
            createdTime={createdTime}
            isConsultant={brandedData?.sourceAttribution?.visibility}
          />
        )}
      </Box>

      {brandedData?.sourceAttribution?.visibility && !IsLarge && (
        <ConsultantStrip jobInfo={jobInfo} />
      )}
      {isBranded && device != 'desktop' && (
        <Box
          mt={brandedData?.sourceAttribution?.visibility ? 1 : -1}
          zIndex={1}
        >
          <StickyJobNavigation
            sections={navigationSections}
            onTabClick={sectionId => {
              console.log('Navigated to section:', sectionId);
            }}
            jobInfo={jobInfo}
          />
        </Box>
      )}
      {/* <ProfileUpdateModal isJobFeed={false} /> */}
      <Box
        display="flex"
        flexDirection={!isMobile ? 'row' : 'column'}
        gap={!isMobile ? 7 : 2}
        px={!isMobile ? 6 : 0}
      >
        <Box sx={{width: {xxs: '100%', md: '55%', lg: '65%', xl: '70%'}}}>
          {!!active && (
            <>
              {!isBranded && (
                <Typography
                  component="h2"
                  mt={3}
                  mb={1.25}
                  fontSize={22}
                  fontWeight={700}
                  ml={!isMobile ? 0 : 1}
                >
                  Job Description
                </Typography>
              )}

              <Box
                ref={jobDescriptionRef}
                className="details-container"
                bgcolor="white"
                mb={!isMobile ? 7 : 0}
                mt={isBranded ? {xxs: 0, md: 2} : 0}
                data-testid="job-description-container"
                borderRadius={isBranded && isSmall ? 0 : 2}
                maxWidth={925}
              >
                {brandedData?.sourceAttribution?.visibility && IsLarge && (
                  <ConsultantStrip jobInfo={jobInfo} />
                )}
                {jobInfo?.confidential === 1 && (
                  <Box
                    display="flex"
                    sx={{
                      background: '#FFFAF0',
                      height: {xxs: '67px', md: '58px'},
                      mt: {xxs: '0px', md: '-8px'},
                      borderRadius: '12px 12px 16px 16px',
                      position: 'relative',
                      p: 1,
                      mx: '2px',
                    }}
                  >
                    <Image
                      className="confidential_icon"
                      src={ConfidentialIcon}
                      alt={'Confidential icon'}
                      width={16}
                      height={20}
                      style={{
                        paddingTop: '6px',
                        objectFit: 'cover',
                      }}
                    />
                    <Box
                      display="flex"
                      sx={{
                        flexDirection: 'column',
                        py: 'auto',
                        px: '4px',
                        justifyContent: 'center',
                        gap: '3px',
                      }}
                    >
                      <Typography
                        sx={{
                          background:
                            'linear-gradient(90deg, #E9630C 0%, #FFAD02 98.08%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          fontWeight: '500',
                          fontSize: {xxs: '13px', md: '14px'},
                          lineHeight: {xxs: '13px', md: '16px'},
                        }}
                      >
                        Confidential Job Posting
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: '#333333',
                          fontWeight: {xxs: '400', md: '500'},
                          lineHeight: {xxs: '13px', md: '16px'},
                        }}
                      >
                        This role is from a verified company that prefers not to
                        disclose its name at this stage.{' '}
                        <span
                          style={{
                            color: '#E9630C',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            setOpenConfJobDialogHirist(true);
                          }}
                        >
                          Learn More
                        </span>
                      </Typography>
                    </Box>
                    <Image
                      className="confidential_verified_tag_hirist"
                      src={ConfidentialTag}
                      alt={'confidential_verified_tag_hirist'}
                      width={136}
                      height={isSmall ? 17 : 21}
                      style={{
                        position: 'absolute',
                        right: isSmall ? '-1px' : '9px',
                        top: '0px',
                      }}
                      objectFit="cover"
                      quality={50}
                    />
                  </Box>
                )}

                <DialogWithBottomSheet
                  title={
                    <Typography
                      fontSize={{xxs: '14px', md: '16px'}}
                      fontWeight={500}
                    >
                      Understanding Confidential Job Postings
                    </Typography>
                  }
                  subTitle=""
                  open={openConfJobDialogHirist}
                  sx={{
                    margin: 'auto',
                    '& .MuiDialog-paper': {
                      minWidth: {
                        md: '500px',
                      },
                      maxWidth: {
                        md: '700px',
                      },
                      width: {md: '580px'},
                    },
                  }}
                  onClose={() => {
                    setOpenConfJobDialogHirist(false);
                  }}
                >
                  <Box
                    display="flex"
                    sx={{
                      flexDirection: 'column',
                      gap: 3,
                      flexWrap: 'wrap',
                      pb: 2,
                      pt: 1.5,
                    }}
                  >
                    <Box
                      display="flex"
                      sx={{
                        flexDirection: 'column',
                        flexWrap: 'wrap',
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle3"
                        sx={{
                          lineHeight: '16px',
                          color: '#333333',
                        }}
                      >
                        What is a confidential job?
                      </Typography>
                      <Typography
                        variant="body3"
                        letterSpacing="0.5px"
                        sx={{
                          lineHeight: '16px',
                          color: '#7E7E7E',
                        }}
                      >
                        A confidential job is one where the hiring company
                        chooses not to reveal its name publicly. This is usually
                        done for sensitive roles, leadership transitions, or
                        strategic hires that require discretion.
                      </Typography>
                    </Box>
                    <Box
                      display="flex"
                      sx={{
                        flexDirection: 'column',
                        flexWrap: 'wrap',
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle3"
                        sx={{
                          lineHeight: '16px',
                          color: '#333333',
                        }}
                      >
                        Why is the company name hidden?
                      </Typography>
                      <Typography
                        variant="body3"
                        letterSpacing="0.5px"
                        sx={{
                          lineHeight: '16px',
                          color: '#7E7E7E',
                        }}
                      >
                        Companies may post jobs confidentially to avoid alerting
                        internal teams, competitors, or clients. It’s a common
                        approach for leadership changes or new roles tied to
                        future business plans. In most cases, the company name
                        is shared once your profile is shortlisted or during the
                        interview process.
                      </Typography>
                    </Box>
                    <Box
                      display="flex"
                      sx={{
                        flexDirection: 'column',
                        flexWrap: 'wrap',
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle3"
                        sx={{
                          lineHeight: '16px',
                          color: '#333333',
                        }}
                      >
                        Can I trust a confidential job?
                      </Typography>
                      <Typography
                        variant="body3"
                        sx={{
                          lineHeight: '16px',
                          color: '#7E7E7E',
                        }}
                        letterSpacing="0.5px"
                      >
                        Yes — all confidential jobs on hirist.tech are posted by
                        verified employers or recruiters. These listings are
                        manually reviewed by our team for quality and
                        authenticity. Your application is only visible to the
                        authorized recruiter or company.
                      </Typography>
                    </Box>
                  </Box>
                </DialogWithBottomSheet>

                <Box py={1} px={3}>
                  {videoUrl.length > 0 && (
                    <Box className="youtubePlay" mt={2}>
                      <iframe
                        src={`https://www.youtube-noCookie.com/embed/${getVideoId(
                          videoUrl
                        )}`}
                        allowFullScreen
                        style={{
                          width: '100%',
                          height: isDeviceLarge || isDeviceMedium ? 432 : 250,
                        }}
                      />
                    </Box>
                  )}
                  <Box sx={{pt: 1.5}}>
                    <Typography
                      component="span"
                      dangerouslySetInnerHTML={{__html: ABDesc || ''}}
                      sx={{
                        fontSize: 16,
                        pt: 1.5,
                        color: 'rgba(0, 0, 0, 0.7)',
                        lineHeight: '24px',

                        '& b': {
                          fontSize: 18,
                          fontWeight: 600,
                          color: '#000',
                          opacity: 1,
                        },

                        '& p': {
                          margin: 0,
                          fontSize: 16,
                          fontFamily: 'inherit',
                          color: 'rgba(0, 0, 0, 0.7)',
                          overflowWrap: 'break-word',

                          '& b': {
                            fontSize: 18,
                            fontWeight: 600,
                            color: '#000',
                            opacity: 1,
                          },

                          '@media (max-width: 1023px)': {
                            fontSize: 16,
                          },
                        },

                        '& p:last-of-type': isBranded
                          ? {
                              display: 'inline',
                            }
                          : {},
                      }}
                    />
                    {isBranded && jobInfo?.introText?.length > 1200 && (
                      <Typography
                        component="span"
                        sx={{
                          color: 'primary.main',
                          cursor: 'pointer',
                          fontSize: 16,
                          fontWeight: 600,
                          ml: 0.5,
                          whiteSpace: 'nowrap',
                        }}
                        onClick={() => {
                          const scrollY = window.scrollY;

                          toggleExpand();
                          setABDesc(
                            !expanded
                              ? jobInfo?.introText
                              : jobInfo?.introText.substr(0, 1200).trim()
                          );
                          if (!expanded) {
                            requestAnimationFrame(() => {
                              window.scrollTo({top: scrollY, behavior: 'auto'});
                            });
                          }
                        }}
                      >
                        {expanded ? 'Read Less' : 'Read More'}
                      </Typography>
                    )}
                  </Box>

                  {testimonials?.length > 0 && (
                    <Diversity data={diversityData} />
                  )}

                  {diversityArray.length > 0 && (
                    <Box
                      mt={testimonials?.length > 0 ? 3 : 6}
                      mb={2}
                      pt={1.5}
                      borderTop="0.1px solid rgba(0, 0, 0, 0.1)"
                      borderBottom="0.1px solid rgba(0, 0, 0, 0.1)"
                      width="calc(100% + 48px)"
                      position="relative"
                      left="-24px"
                      pl={3}
                    >
                      <Typography
                        sx={{opacity: 0.7}}
                        display="inline-block"
                        mr={1}
                        fontSize={14}
                      >
                        The job is for:{' '}
                      </Typography>
                      {diversityArray.map((data, idx) => (
                        <Box
                          sx={{opacity: 0.4, fontSize: 14}}
                          key={idx}
                          display="inline-block"
                          border="1px solid rgb(224, 224, 224)"
                          borderRadius="16px"
                          px={2}
                          height={32}
                          lineHeight={2}
                          mb={1.5}
                          mr={0.5}
                        >
                          {data}
                        </Box>
                      ))}
                    </Box>
                  )}
                  {(!isBranded || isSmall) && (
                    <ApplyBtns
                      handleApply={handleApply}
                      saveJobForLogged={saveJobForLogged}
                      openInsightsDialog={openInsightsDialog}
                      handleJobFollowup={handleJobFollowup}
                      jobSaved={!!jobInfo.saved}
                      jobApplied={jobApplied}
                      jobFollowedUp={followup}
                      jobId={jobId}
                      showApply={!isMobile}
                      showLoader={showLoader}
                      setShowLoader={setShowLoader}
                      setApplyBtnClicked={setApplyBtnClicked}
                    />
                  )}
                  <Box
                    sx={{
                      borderTop: '1px solid #F0F0F0',
                      width: 'calc(100% + 48px)',
                      position: 'relative',
                      left: '-24px',
                      my: 2,
                    }}
                  />
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Image
                        src={HiristInfoIcon}
                        alt="info-icon"
                        height={18}
                        width={18}
                      />
                      <Typography
                        fontSize={14}
                        fontWeight={300}
                        height="fit-content"
                        mt={0.3}
                        ml={0.3}
                      >
                        Did you find something suspicious?{' '}
                        <Button
                          onClick={() => {
                            setTimeout(() => {
                              setOpenReportJobDialog(true);
                            }, 0);
                          }}
                          sx={{
                            color: 'rgb(0, 87, 255)',
                            fontWeight: 700,
                            p: 0,
                          }}
                          disableRipple
                        >
                          Report Here
                        </Button>
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
              {isBranded && brandedData?.aboutCompany?.visibility && (
                <Box
                  mb={!isMobile ? 4 : 0}
                  data-testid="job-description-container"
                  borderRadius="8px"
                  display={{xxs: 'none', md: 'block'}}
                  sx={{
                    mt: -4,
                    maxWidth: 925,
                  }}
                >
                  <Box ref={aboutCompanyRef}>
                    <AboutUsSection
                      aboutCompany={brandedData?.aboutCompany || {}}
                      url={jobInfo?.showcase?.showcaseUrl || ''}
                      templateInfo={brandedData?.templateInfo}
                      sourceAttr={
                        brandedData?.sourceAttribution?.sourceAttributionText
                      }
                      jobInfo={jobInfo}
                      jdQueryParams={{ref: ref || '', jobPos: jobPos || ''}}
                      isAppIimjobs={isAppIimjobs}
                    />
                  </Box>
                </Box>
              )}
            </>
          )}

          {!isMobile &&
            (jobFeedStore?.jobfeed?.length > 0 ||
              jobFeedStore?.isLoading == true) &&
            !isBranded && (
              <Box mb={{xxs: 2, md: 0}} maxWidth={925}>
                <JobFeedHarness
                  routeToJobDetail={routeToJobDetail}
                  title={
                    <Typography
                      component="h2"
                      fontSize={16}
                      mb={1.5}
                      mt={!active ? 6 : 0}
                    >
                      Similar jobs that you might be interested in
                    </Typography>
                  }
                  feedOnly={true}
                  infiniteScroll={false}
                  injectAsChild={true}
                  showSmallBadges={false}
                  showProfileCard={false}
                  variant={VersionVariants.VERSION_TWO}
                  onJobSave={() => {}}
                />
              </Box>
            )}
        </Box>

        <Box
          mt={!isMobile && !!active ? (isBranded ? 2 : 3) : 0}
          pt={!isMobile ? (isBranded ? 0 : 5.5) : 0}
          maxWidth={!isMobile ? 365 : '100%'}
        >
          <Box
            bgcolor="white"
            p={2}
            borderRadius={isBranded && isSmall ? 0 : 2}
            mb={isBranded ? 2 : 3}
          >
            <JobOverviewCard
              actionUrl={
                !jobInfo?.confidential
                  ? `/r/${jobInfo?.recruiter?.recruiterId}-${cleanUrl(
                      jobInfo?.recruiter?.recruiterName.replace(/&/g, '-and-')
                    )}`
                  : '#'
              }
              variant={VersionVariants.VERSION_TWO}
              confidential={jobInfo.confidential === 1}
              jobId={jobId}
              jobViews={hits}
              applications={applyCount}
              recruiterActions={recruiterActions}
              magicSortRank={magicRank}
              category={{label: categoryName, tag: categoryName}}
              lastActive={
                lastActive
                  ? moment(lastActive).isValid()
                    ? moment(lastActive).format('D MMM YYYY')
                    : lastActive
                  : ''
              }
              position={confidential ? '' : `${designation} at ${companyName}`}
              recruiterName={
                confidential ? 'Verified Recruiter' : recruiterName
              }
              recruiterImgUrl={
                confidential ? '/resources/img/user.jpg' : profilePicUrl
              }
              enableMagicRank={1}
              disableInsights
              functionalArea={
                HIRIST_FUNCTIONAL_AREAS.find(fa => fa.value === functionalArea)
                  ?.label
              }
              showRecommended
              postedOn={moment(createdTime).toISOString()}
              setOpenConfJobDialog={setOpenConfJobDialogHirist}
              showReadMore={isBranded}
              isExpanded={jobOverviewExpanded}
              onToggle={toggleJobOverview}
            />
          </Box>
          {isBranded ? (
            <Box
              mb={!isMobile ? 4 : 0}
              data-testid="job-description-container"
              borderRadius="8px"
              display={{xxs: 'block', md: 'none'}}
              sx={{
                mt: {xxs: -0.5, md: -4},
                maxWidth: 925,
              }}
            >
              {brandedData?.aboutCompany?.visibility && (
                <Box ref={aboutCompanyRef}>
                  <AboutUsSection
                    aboutCompany={brandedData?.aboutCompany || {}}
                    url={jobInfo?.showcase?.showcaseUrl || ''}
                    templateInfo={brandedData?.templateInfo}
                    sourceAttr={
                      brandedData?.sourceAttribution?.sourceAttributionText
                    }
                    jobInfo={jobInfo}
                    jdQueryParams={{ref: ref || '', jobPos: jobPos || ''}}
                    isAppIimjobs={isAppIimjobs}
                  />
                </Box>
              )}
              {jobInfo && jobInfo?.id && (
                <Box ref={companyJobsRef} display={{xxs: 'block', md: 'none'}}>
                  {getCompanyJobs()}
                </Box>
              )}
              {brandedData?.clients?.visibility && (
                <Box display={{xxs: 'block', md: 'none'}}>{getClients()}</Box>
              )}

              {brandedData?.keyHighlights?.visibility && (
                <Box
                  ref={companyHighlightsRef}
                  display={{xxs: 'block', md: 'none'}}
                >
                  {getHighLights()}
                </Box>
              )}
              {brandedData?.hiringTrends?.visibility && (
                <Box ref={InsightsRef} display={{xxs: 'block', md: 'none'}}>
                  {getInsights()}
                </Box>
              )}
              {brandedData?.awardsRecognitions?.visibility && (
                <Box
                  ref={companyAwardsRef}
                  display={{xxs: 'block', md: 'none'}}
                >
                  {getAwardsCard()}
                </Box>
              )}
              {brandedData?.benefits?.visibility && (
                <Box
                  ref={companyBenefitsRef}
                  display={{xxs: 'block', md: 'none'}}
                >
                  {getVerifiedBenefitsCard()}
                </Box>
              )}
              {brandedData?.reviews?.visibility && (
                <Box
                  ref={companyReviewsRef}
                  display={{xxs: 'block', md: 'none'}}
                >
                  {getCompanyReview()}
                </Box>
              )}
            </Box>
          ) : (
            <></>
          )}
          {!isMobile &&
            (!isBranded ? (
              <Box>
                <AdsPanelHarness
                  variant={VersionVariants.VERSION_TWO}
                  showSmallBadges={false}
                  showFeatured={false}
                  showLearning={true}
                  showLearningCenterCTA={false}
                  showLearnGrow={true}
                  showEventOnly={true}
                  page={JOBDETAIL}
                />
                <JobsByLocation />
                <DownloadAppJobdetail sendEvent={sendEvent} />
                <Box mt={3.75}>
                  <BlogsPanel heading="Interview Questions for you" />
                </Box>
              </Box>
            ) : (
              <>
                {jobInfo && jobInfo?.id && (
                  <Box display={{xxs: 'none', md: 'block'}}>
                    {getCompanyJobs()}
                  </Box>
                )}
                {brandedData?.clients?.visibility && (
                  <Box display={{xxs: 'none', md: 'block'}}>{getClients()}</Box>
                )}
                {brandedData?.keyHighlights?.visibility && (
                  <Box display={{xxs: 'none', md: 'block'}}>
                    {getHighLights()}
                  </Box>
                )}
                {brandedData?.hiringTrends?.visibility && (
                  <Box display={{xxs: 'none', md: 'block'}}>
                    {getInsights()}
                  </Box>
                )}
                {brandedData?.awardsRecognitions?.visibility && (
                  <Box display={{xxs: 'none', md: 'block'}}>
                    {getAwardsCard()}
                  </Box>
                )}
                {brandedData?.benefits?.visibility && (
                  <Box
                    display={{xxs: 'none', md: 'block'}}
                    sx={{
                      mb: brandedData?.reviews?.visibility ? 0 : 4,
                    }}
                  >
                    {getVerifiedBenefitsCard()}
                  </Box>
                )}
                {brandedData?.reviews?.visibility && (
                  <Box display={{xxs: 'none', md: 'block'}}>
                    {getCompanyReview()}
                  </Box>
                )}
              </>
            ))}
        </Box>
      </Box>
      {enableLoginPopup && (
        <HiristAuthentication
          variant={VersionVariants.VERSION_TWO}
          onSuccess={onLoginSuccess}
          openDialog={enableLoginPopup}
          enableSignIn={true}
          setOpenDialog={() => {
            setShowLoader(false);
            dispatch({
              type: 'AppContext/set_login_popup',
              payload: false,
            });
          }}
        />
      )}

      {insightsOpen && (
        <InsightsModal
          handleClose={() => setInsightsOpen(false)}
          open={insightsOpen}
          variant={VersionVariants.VERSION_TWO}
          proUser={isPro}
          proMeta={proMembership?.insightsBanner}
          setOpenConfJobDialog={setOpenConfJobDialogHirist}
        />
      )}

      {openReportJobDialog && (
        <ReportJobDialog
          open={openReportJobDialog}
          onClose={() => setOpenReportJobDialog(false)}
          variant={VersionVariants.VERSION_THREE}
        />
      )}

      {/* {openResumeBanner && (
        <ResumePopup
          open={openResumeBanner}
          handleCTA={() => {
            console.log('Handled Called');
            setResumeBannerSuccess(true);
          }}
          onClose={() => setOpenResumeBanner(false)}
        />
      )} */}
      {openResumeBanner && (
        <ProfileUpdateModal
          isJobFeed={false}
          handleForceBannerClose={() => {
            setOpenResumeBanner(false);
          }}
          forceEnable={openResumeBanner}
        />
      )}
      {isProfileUpdateModalOpen && (
        <ProfileUpdateModal
          isJobFeed={false}
          handleForceBannerClose={() => {
            setIsProfileUpdateModalOpen(false);
          }}
          forceEnable={isProfileUpdateModalOpen}
          isProfileUpdateModalOpen={isProfileUpdateModalOpen}
        />
      )}

      {!isDeviceLarge &&
        isMobile &&
        jobFeedStore?.jobfeed?.length > 0 &&
        !isBranded && (
          <Box
            mb={{xxs: 2, md: 0}}
            position="relative"
            left={{xxs: 0, md: '-40px'}}
          >
            <JobFeedHarness
              routeToJobDetail={routeToJobDetail}
              title={
                <Typography mt={2}>
                  Similar jobs that you might be interested in
                </Typography>
              }
              feedOnly={true}
              infiniteScroll={false}
              injectAsChild={true}
              showSmallBadges={false}
              showProfileCard={false}
              variant={VersionVariants.VERSION_TWO}
            />
          </Box>
        )}
      {isBranded && isLargeDevice && (
        <Box
          display="flex"
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,

            bgcolor: 'var(--Colors-Alice-Blue-500, #F3F7FA);',
            border: '1px solid var(--Colors-Alice-Blue-500, #F3F7FA)',
            boxShadow:
              '0 2px 2px 0 rgba(0, 0, 0, 0.08), 0 6.48px 6px 0 rgba(41, 41, 41, 0.06) inset;',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            px: 0,
            py: 2.5,

            zIndex: 1200,
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            sx={{
              maxWidth: 1440,
              alignItems: 'center',
              width: '100%',
              px: 6,
            }}
          >
            <Box display={'flex'} alignItems={'center'}>
              <Box
                borderRadius={4}
                height={50}
                width={50}
                sx={{
                  '.joblist__logo': {
                    borderRadius: 2,
                    overflow: 'hidden',
                    background: jobInfo?.companyData?.companyLogoFilePath
                      ? '#fff'
                      : 'auto',
                    boxShadow: 2,
                    border: '0.6px solid #7E7E7E33',
                  },
                }}
              >
                {jobInfo?.companyData?.companyLogoFilePath ? (
                  <ImageWithFallback
                    className="joblist__logo"
                    src={jobInfo?.companyData?.companyLogoFilePath ?? ''}
                    fallback={
                      jobInfo?.recruiter?.centralizedCompanyId &&
                      !jobInfo?.confidential
                        ? `${process.env.NEXT_PUBLIC_S3_COMPANY_LOGO_BUCKET}${jobInfo?.recruiter?.centralizedCompanyId}.webp`
                        : ''
                    }
                    placeholder="blur"
                    blurDataURL={`${process.env.NEXT_PUBLIC_S3_COMPANY_LOGO_BUCKET}${jobInfo?.recruiter?.centralizedCompanyId}.webp`}
                    alt={
                      `${jobInfo?.companyData?.companyName?.replace(
                        /\s+/g,
                        ''
                      )}_logo'` || ''
                    }
                    width={50}
                    height={50}
                    object-fit="cover"
                    quality={50}
                    // placeholder="blur"
                    itemId={jobInfo?.id}
                    // loader={imageLoader}
                  />
                ) : (
                  <Box
                    className="joblist__logo"
                    sx={{
                      background: randomColor,
                      p: 1.5,

                      height: 'min-content',
                      lineHeight: 0,
                    }}
                  >
                    <Image
                      src={DefaultJobLogo}
                      alt={'Logo'}
                      width={26}
                      height={26}
                      objectFit="cover"
                      quality={50}
                    />
                  </Box>
                )}
              </Box>
              <Box ml={1}>
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 600,
                    display: '-webkit-box',
                    WebkitLineClamp: 1, // change to 2 if you want two-line truncation
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {jobInfo?.title.split('(')[0]}
                </Typography>
                <Box display={'flex'} alignItems={'center'}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    {`${jobInfo?.min} - ${jobInfo?.max} yrs`}{' '}
                  </Typography>
                  <Image
                    src={Dot}
                    width={4}
                    height={4}
                    alt="."
                    style={{margin: '2px 8px'}}
                  />
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    {getLocationString(jobInfo?.locations)}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box width={440} mt={-2.5}>
              {' '}
              <ApplyBtns
                handleApply={handleApply}
                saveJobForLogged={saveJobForLogged}
                openInsightsDialog={openInsightsDialog}
                handleJobFollowup={handleJobFollowup}
                jobSaved={!!jobInfo.saved}
                jobApplied={jobApplied}
                jobFollowedUp={followup}
                jobId={jobId}
                showApply={!isMobile}
                showLoader={showLoader}
                setShowLoader={setShowLoader}
                setApplyBtnClicked={setApplyBtnClicked}
                branded={isBranded}
              />
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default HiristJobDetail;
